import type {
  AppointmentPlanningCommitResponse,
  AppointmentPlanningPreviewRow,
} from "../../../../../../../../api/kehrblatt/planning"
import type {
  AppointmentPlanningCommittedSubmissionResult,
  AppointmentPlanningExistingPlannedRow,
  AppointmentPlanningSubmitIntent,
} from "../../../../../_logic/appointments-contract"

const buildExistingPlannedRows = ({
  planningRows,
  previouslyPlannedRows,
  previewByRowId,
  response,
}: {
  planningRows: Extract<AppointmentPlanningSubmitIntent, { kind: "commit" }>["planningRows"]
  previouslyPlannedRows: Extract<
    AppointmentPlanningSubmitIntent,
    { kind: "commit" }
  >["previouslyPlannedRows"]
  previewByRowId: ReadonlyMap<string, AppointmentPlanningPreviewRow>
  response: AppointmentPlanningCommitResponse
}): AppointmentPlanningExistingPlannedRow[] => {
  const scheduledByWorkOrderId = new Map(
    (response.scheduled_events ?? [])
      .filter((event) => event.work_order_id)
      .map((event) => [String(event.work_order_id), event]),
  )
  const rowResultById = new Map(
    (response.row_results ?? []).flatMap((result) => {
      const keys = [result.client_row_id, result.work_order_id].map(String).filter(Boolean)

      return keys.map((key) => [key, result] as const)
    }),
  )
  const failedRowIds = new Set(
    (response.failed ?? [])
      .flatMap((result) => [result.client_row_id, result.work_order_id])
      .map(String)
      .filter(Boolean),
  )
  const newlyPlannedRows = planningRows.flatMap((row): AppointmentPlanningExistingPlannedRow[] => {
    const event = scheduledByWorkOrderId.get(row.workOrder.id) ?? null
    const rowResult = rowResultById.get(row.id) ?? rowResultById.get(row.workOrder.id) ?? null
    const failed =
      failedRowIds.has(row.id) ||
      failedRowIds.has(row.workOrder.id) ||
      rowResult?.status === "failed"

    if (failed || (!event && rowResult?.status !== "planned")) return []

    return [
      {
        id: row.id,
        source: row,
        preview: previewByRowId.get(row.id) ?? previewByRowId.get(row.workOrder.id) ?? null,
        event,
      },
    ]
  })
  const plannedByRowId = new Map(
    previouslyPlannedRows
      .filter((row) => Boolean(row.event || row.preview))
      .map((row) => [row.id, row]),
  )
  for (const row of newlyPlannedRows) plannedByRowId.set(row.id, row)

  return [...plannedByRowId.values()]
}

export const projectAppointmentPlanningCommitOutcome = <TCommitReceipt>({
  intent,
  response,
  receipt,
}: {
  intent: Extract<AppointmentPlanningSubmitIntent, { kind: "commit" }>
  response: AppointmentPlanningCommitResponse
  receipt: TCommitReceipt
}): AppointmentPlanningCommittedSubmissionResult<TCommitReceipt> => {
  const plannedRows = buildExistingPlannedRows({
    planningRows: intent.planningRows,
    previouslyPlannedRows: intent.previouslyPlannedRows,
    previewByRowId: intent.previewByRowId,
    response,
  })
  const completedRowIds = new Set(plannedRows.map((row) => row.id))
  const currentRowsCompleted = intent.planningRows.every((row) => completedRowIds.has(row.id))

  return {
    kind: "committed",
    status: currentRowsCompleted && !(response.failed ?? []).length ? "completed" : "partial",
    result: {
      settings: intent.settings,
      plannedRows,
      response,
      committed: true,
    },
    response,
    receipt,
    removedRowIds: [...intent.removedRowIds],
  }
}
